<template>
  <app-page
    :title="program?.program_name || 'Award'"
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
    back-route="/recognition"
  >
    <template #header-content>
      <app-stat
        v-if="program && program.budget_minor"
        class="budget-stat"
        title="Budget"
        :stat="budgetRemaining"
        :formatter="formatCurrency"
        subtitle="remaining"
        small
      >
        <b-progress :value="budgetSpent" :max="program.budget_minor" class="budget-progress mt-2 mb-2" />
        <div class="budget-footer">
          <span
            ><strong>{{ formatCurrency(program.budget_minor) }}</strong> Budget</span
          >
          <span :class="budgetSpentClass">
            <strong>{{ formatCurrency(budgetSpent) }}</strong> Spent
          </span>
        </div>
      </app-stat>
    </template>

    <template v-if="program && !isMilestone" #after-header>
      <b-row no-gutters class="justify-content-end mt-4">
        <b-button variant="primary" v-b-modal.give-recognition-modal>Give recognition</b-button>
      </b-row>
    </template>

    <template #below-header>
      <div v-if="program" class="award-status">
        <b-form-checkbox
          :checked="program.is_active"
          switch
          size="lg"
          class="award-status__toggle"
          :disabled="isTogglingActive"
          @click.native.prevent="onToggleActive"
        />
        <span class="award-status__label">
          Status: This award is <strong>{{ program.is_active ? 'Active' : 'Disabled' }}</strong>
        </span>
      </div>
    </template>

    <template #custom-content>
      <app-loading :loading="isLoading" is-background />

      <template v-if="!isLoading && program">
        <b-col>
          <sme-card large>
            <navigable-tabs content-class="mt-3">
              <b-tab id="setup">
                <template #title>
                  <span>Setup</span>
                </template>
                <open-award-setup-tab v-if="isOpen" :program="program" @updated="onProgramUpdated" />
                <program-award-setup-tab v-else-if="isProgram" :program="program" @updated="onProgramUpdated" />
                <milestone-award-setup-tab v-else-if="isMilestone" :program="program" @updated="onProgramUpdated" />
              </b-tab>

              <b-tab id="pending-approval" @shown="onPendingTabShown">
                <template #title>
                  <span>
                    Pending approval
                    <b-badge v-if="pendingNominations.length > 0" pill variant="primary" class="ml-1">
                      {{ pendingNominations.length }}
                    </b-badge>
                  </span>
                </template>
                <pending-approvals-table
                  :nominations="pendingNominations"
                  :loading="isLoadingPendingNominations"
                  :show-program-column="false"
                  @review="onReviewNomination"
                />
              </b-tab>

              <b-tab id="upcoming" v-if="isMilestone" @shown="onUpcomingTabShown">
                <template #title>
                  <span>
                    Upcoming
                    <b-badge v-if="upcomingAwards.length > 0" pill variant="primary" class="ml-1">
                      {{ upcomingAwards.length }}
                    </b-badge>
                  </span>
                </template>
                <upcoming-awards-table :awards="upcomingAwards" :loading="isLoadingUpcomingAwards" />
              </b-tab>

              <b-tab id="analytics" @shown="onAnalyticsTabShown">
                <template #title>
                  <span>Analytics</span>
                </template>
                <analytics-tab v-if="analyticsTabShown" :programs="programs" :program-id="programId" />
              </b-tab>

              <b-tab id="transactions" @shown="onTransactionsTabShown">
                <template #title>
                  <span>Transactions</span>
                </template>
                <transactions-table v-if="transactionsTabShown" :programs="programs" :program-id="programId" />
              </b-tab>
            </navigable-tabs>
          </sme-card>
        </b-col>
      </template>

      <template v-if="!isLoading && !program">
        <b-col>
          <b-card class="text-center p-5">
            <p class="text-muted">Award not found.</p>
            <router-link :to="{ name: 'recognition' }">Return to Recognition</router-link>
          </b-card>
        </b-col>
      </template>
    </template>

    <template v-slot:footer>
      <review-nomination-modal
        ref="reviewModalRef"
        :nomination="reviewingNomination"
        :programs="programs"
        @approved="onNominationReviewed"
        @rejected="onNominationReviewed"
      />
      <confirm-disable-award-modal
        ref="confirmDisableModalRef"
        :program-name="program?.program_name || ''"
        @confirmed="onDisableConfirmed"
      />
      <give-recognition-modal :programs="programs" :initial-program="program" @awarded="onAwardGiven" />
    </template>
  </app-page>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import AppPage from '@/components/AppPage.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import PendingApprovalsTable from '@/pages/recognition/components/PendingApprovalsTable.vue';
import ReviewNominationModal from '@/pages/recognition/components/ReviewNominationModal.vue';
import ConfirmDisableAwardModal from '@/pages/recognition/components/ConfirmDisableAwardModal.vue';
import GiveRecognitionModal from '@/pages/recognition/components/GiveRecognitionModal.vue';
import UpcomingAwardsTable from '@/pages/recognition/components/UpcomingAwardsTable.vue';
import TransactionsTable from '@/pages/recognition/components/TransactionsTable.vue';
import AnalyticsTab from '@/pages/recognition/components/AnalyticsTab.vue';
import OpenAwardSetupTab from '@/pages/recognition/award-types/open/OpenAwardSetupTab.vue';
import ProgramAwardSetupTab from '@/pages/recognition/award-types/program/ProgramAwardSetupTab.vue';
import MilestoneAwardSetupTab from '@/pages/recognition/award-types/milestone/MilestoneAwardSetupTab.vue';
import { toCurrency } from '@/utils';
import ApiClient from '@/ApiClient';
import State from '@/state/State';
import useToast from '@/composables/useToast';

const { doToast, TOAST_VARIANT } = useToast();

const props = defineProps({
  programId: {
    type: String,
    required: true,
  },
});

const reviewModalRef = ref(null);
const confirmDisableModalRef = ref(null);
const reviewingNomination = ref(null);
const isTogglingActive = ref(false);
const program = ref(null);
const programs = ref([]);
const pendingNominations = ref([]);
const upcomingAwards = ref([]);
const isLoadingProgram = ref(true);
const isLoadingPendingNominations = ref(false);
const isLoadingUpcomingAwards = ref(false);
const pendingNominationsLoaded = ref(false);
const upcomingAwardsLoaded = ref(false);
const analyticsTabShown = ref(false);
const transactionsTabShown = ref(false);

const isLoading = computed(() => isLoadingProgram.value);
const currency = computed(() => State.state?.company?.default_currency);

const isMilestone = computed(() => {
  return program.value?.program_type === 'MILESTONE';
});

const isOpen = computed(() => {
  return program.value?.program_type === 'OPEN';
});

const isProgram = computed(() => {
  return program.value?.program_type === 'PROGRAM';
});

const budgetSpent = computed(() => {
  if (!program.value?.budget_minor) {
    return 0;
  }
  const properties = program.value.properties || {};
  return properties.budget_spent_minor || 0;
});

const budgetSpentClass = computed(() => {
  if (!program.value?.budget_minor) {
    return 'budget-footer__spent--success';
  }
  const budget = program.value.budget_minor;
  if (budgetSpent.value > budget) {
    return 'budget-footer__spent--danger';
  }
  if (budgetSpent.value >= budget * 0.9) {
    return 'budget-footer__spent--warning';
  }
  return 'budget-footer__spent--success';
});

const budgetRemaining = computed(() => {
  if (!program.value?.budget_minor) {
    return 0;
  }
  return program.value.budget_minor - budgetSpent.value;
});

const formatCurrency = value => {
  return toCurrency(value, currency.value).replace(/\.00$/, '');
};

const getProgram = async () => {
  isLoadingProgram.value = true;
  try {
    const result = await ApiClient.getRecognitionProgram(props.programId);
    program.value = result;
    // Create a single-item array for the TransactionsTable programs prop
    programs.value = [result];
  } catch (e) {
    program.value = null;
    programs.value = [];
  } finally {
    isLoadingProgram.value = false;
  }
};

const getPendingNominations = async () => {
  isLoadingPendingNominations.value = true;
  try {
    const nominations = await ApiClient.getPendingNominations(props.programId);
    pendingNominations.value = nominations || [];
    pendingNominationsLoaded.value = true;
  } catch (e) {
    pendingNominations.value = [];
  } finally {
    isLoadingPendingNominations.value = false;
  }
};

const onPendingTabShown = () => {
  if (!pendingNominationsLoaded.value) {
    getPendingNominations();
  }
};

const getUpcomingAwards = async () => {
  isLoadingUpcomingAwards.value = true;
  try {
    const awards = await ApiClient.getUpcomingMilestoneAwards(props.programId);
    upcomingAwards.value = awards || [];
    upcomingAwardsLoaded.value = true;
  } catch (e) {
    upcomingAwards.value = [];
  } finally {
    isLoadingUpcomingAwards.value = false;
  }
};

const onUpcomingTabShown = () => {
  if (!upcomingAwardsLoaded.value) {
    getUpcomingAwards();
  }
};

const onAnalyticsTabShown = () => {
  analyticsTabShown.value = true;
};

const onTransactionsTabShown = () => {
  transactionsTabShown.value = true;
};

const onAwardGiven = () => {
  loadAllData();
};

const onProgramUpdated = () => {
  // Refresh program data after successful update
  getProgram();
};

const onReviewNomination = nomination => {
  reviewingNomination.value = nomination;
  reviewModalRef.value?.show();
};

const onNominationReviewed = () => {
  getProgram();
  getPendingNominations();
};

const onToggleActive = () => {
  if (program.value?.is_active) {
    // Disabling -- show confirmation
    confirmDisableModalRef.value?.show();
  } else {
    // Enabling -- no confirmation needed
    updateActiveStatus(true);
  }
};

const onDisableConfirmed = () => {
  updateActiveStatus(false);
};

const updateActiveStatus = async isActive => {
  isTogglingActive.value = true;
  try {
    await ApiClient.updateRecognitionProgram(props.programId, { is_active: isActive });
    doToast('Success', `Award ${isActive ? 'enabled' : 'disabled'} successfully`, TOAST_VARIANT.SUCCESS);
    await getProgram();
  } catch (e) {
    const message = e?.response?.data?.message || e?.message || `Failed to ${isActive ? 'enable' : 'disable'} award`;
    doToast('Error', message, TOAST_VARIANT.DANGER);
  } finally {
    isTogglingActive.value = false;
  }
};

const loadAllData = async () => {
  pendingNominationsLoaded.value = false;
  upcomingAwardsLoaded.value = false;
  analyticsTabShown.value = false;
  transactionsTabShown.value = false;

  await Promise.all([getProgram(), getPendingNominations()]);
  if (isMilestone.value) {
    getUpcomingAwards();
  }
};

// Watch for programId changes (Vue Router may reuse this component)
watch(
  () => props.programId,
  () => {
    loadAllData();
  },
);

onMounted(() => {
  loadAllData();
});
</script>

<style scoped lang="scss">
:deep(.page-sub-header-wrapper) {
  align-self: flex-start;
}

.budget-stat {
  min-width: 17.5rem;

  :deep(.app-stat__title),
  :deep(.app-stat__value) {
    color: var(--palette-color-default);
  }

  :deep(.app-stat__subtitle) {
    color: var(--palette-color-default-lighten-30);
  }
}

.budget-progress {
  height: 0.5rem;
  border-radius: 0.25rem;
  background-color: var(--palette-color-core-1-lighten-80);

  :deep(.progress-bar) {
    background-color: var(--palette-color-core-1);
  }
}

.budget-footer {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--palette-color-default-lighten-30);

  strong {
    color: var(--palette-color-default);
  }

  &__spent--success {
    strong {
      color: var(--palette-color-success);
    }
  }

  &__spent--warning {
    strong {
      color: var(--palette-color-warning);
    }
  }

  &__spent--danger {
    strong {
      color: var(--palette-color-danger);
    }
  }
}

.award-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;

  &__toggle {
    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
      background-color: var(--palette-color-brand-primary);
      border-color: var(--palette-color-brand-primary);
    }
  }

  &__label {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);

    strong {
      color: var(--palette-color-default);
    }
  }
}

.tab-placeholder {
  padding: 3rem;
  text-align: center;
}
</style>
